/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationOptions, ɵStyleData} from '@angular/animations';

import {AnimationDriver} from '../render/animation_driver';
import {getOrSetAsInMap} from '../render/shared';
import {copyObj, interpolateParams, iteratorToArray, mergeAnimationOptions} from '../util';

import {StyleAst, TransitionAst} from './animation_ast';
import {buildAnimationTimelines} from './animation_timeline_builder';
import {TransitionMatcherFn} from './animation_transition_expr';
import {AnimationTransitionInstruction, createTransitionInstruction} from './animation_transition_instruction';
import {ElementInstructionMap} from './element_instruction_map';

const /** @type {?} */ EMPTY_OBJECT = {};
export class AnimationTransitionFactory {
/**
 * @param {?} _triggerName
 * @param {?} ast
 * @param {?} _stateStyles
 */
constructor(
private _triggerName: string,
public ast: TransitionAst,
private _stateStyles: {[stateName: string]: AnimationStateStyles}) {}
/**
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
match(currentState: any, nextState: any): boolean {
    return oneOrMoreTransitionsMatch(this.ast.matchers, currentState, nextState);
  }
/**
 * @param {?} stateName
 * @param {?} params
 * @param {?} errors
 * @return {?}
 */
buildStyles(stateName: string, params: {[key: string]: any}, errors: any[]) {
    const /** @type {?} */ backupStateStyler = this._stateStyles['*'];
    const /** @type {?} */ stateStyler = this._stateStyles[stateName];
    const /** @type {?} */ backupStyles = backupStateStyler ? backupStateStyler.buildStyles(params, errors) : {};
    return stateStyler ? stateStyler.buildStyles(params, errors) : backupStyles;
  }
/**
 * @param {?} driver
 * @param {?} element
 * @param {?} currentState
 * @param {?} nextState
 * @param {?=} currentOptions
 * @param {?=} nextOptions
 * @param {?=} subInstructions
 * @return {?}
 */
build(
      driver: AnimationDriver, element: any, currentState: any, nextState: any,
      currentOptions?: AnimationOptions, nextOptions?: AnimationOptions,
      subInstructions?: ElementInstructionMap): AnimationTransitionInstruction {
    const /** @type {?} */ errors: any[] = [];

    const /** @type {?} */ transitionAnimationParams = this.ast.options && this.ast.options.params || EMPTY_OBJECT;
    const /** @type {?} */ currentAnimationParams = currentOptions && currentOptions.params || EMPTY_OBJECT;
    const /** @type {?} */ currentStateStyles = this.buildStyles(currentState, currentAnimationParams, errors);
    const /** @type {?} */ nextAnimationParams = nextOptions && nextOptions.params || EMPTY_OBJECT;
    const /** @type {?} */ nextStateStyles = this.buildStyles(nextState, nextAnimationParams, errors);

    const /** @type {?} */ queriedElements = new Set<any>();
    const /** @type {?} */ preStyleMap = new Map<any, {[prop: string]: boolean}>();
    const /** @type {?} */ postStyleMap = new Map<any, {[prop: string]: boolean}>();
    const /** @type {?} */ isRemoval = nextState === 'void';

    const /** @type {?} */ animationOptions = {params: {...transitionAnimationParams, ...nextAnimationParams}};

    const /** @type {?} */ timelines = buildAnimationTimelines(
        driver, element, this.ast.animation, currentStateStyles, nextStateStyles, animationOptions,
        subInstructions, errors);

    if (errors.length) {
      return createTransitionInstruction(
          element, this._triggerName, currentState, nextState, isRemoval, currentStateStyles,
          nextStateStyles, [], [], preStyleMap, postStyleMap, errors);
    }

    timelines.forEach(tl => {
      const /** @type {?} */ elm = tl.element;
      const /** @type {?} */ preProps = getOrSetAsInMap(preStyleMap, elm, {});
      tl.preStyleProps.forEach(prop => preProps[prop] = true);

      const /** @type {?} */ postProps = getOrSetAsInMap(postStyleMap, elm, {});
      tl.postStyleProps.forEach(prop => postProps[prop] = true);

      if (elm !== element) {
        queriedElements.add(elm);
      }
    });

    const /** @type {?} */ queriedElementsList = iteratorToArray(queriedElements.values());
    return createTransitionInstruction(
        element, this._triggerName, currentState, nextState, isRemoval, currentStateStyles,
        nextStateStyles, timelines, queriedElementsList, preStyleMap, postStyleMap);
  }
}

function AnimationTransitionFactory_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTransitionFactory.prototype._triggerName;
/** @type {?} */
AnimationTransitionFactory.prototype.ast;
/** @type {?} */
AnimationTransitionFactory.prototype._stateStyles;
}

/**
 * @param {?} matchFns
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
function oneOrMoreTransitionsMatch(
    matchFns: TransitionMatcherFn[], currentState: any, nextState: any): boolean {
  return matchFns.some(fn => fn(currentState, nextState));
}
export class AnimationStateStyles {
/**
 * @param {?} styles
 * @param {?} defaultParams
 */
constructor(private styles: StyleAst,
private defaultParams: {[key: string]: any}) {}
/**
 * @param {?} params
 * @param {?} errors
 * @return {?}
 */
buildStyles(params: {[key: string]: any}, errors: string[]): ɵStyleData {
    const /** @type {?} */ finalStyles: ɵStyleData = {};
    const /** @type {?} */ combinedParams = copyObj(this.defaultParams);
    Object.keys(params).forEach(key => {
      const /** @type {?} */ value = params[key];
      if (value != null) {
        combinedParams[key] = value;
      }
    });
    this.styles.styles.forEach(value => {
      if (typeof value !== 'string') {
        const /** @type {?} */ styleObj = /** @type {?} */(( value as any));
        Object.keys(styleObj).forEach(prop => {
          let /** @type {?} */ val = styleObj[prop];
          if (val.length > 1) {
            val = interpolateParams(val, combinedParams, errors);
          }
          finalStyles[prop] = val;
        });
      }
    });
    return finalStyles;
  }
}

function AnimationStateStyles_tsickle_Closure_declarations() {
/** @type {?} */
AnimationStateStyles.prototype.styles;
/** @type {?} */
AnimationStateStyles.prototype.defaultParams;
}

